import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Mail,
  Phone,
  Lock,
  User,
  Eye,
  EyeOff,
  ShieldCheck,
  Sparkles,
  Leaf,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { readError } from "@/lib/api";
import { useSiteImage } from "@/hooks/useSiteImages";

const HERITAGE_IMG_DEFAULT =
  "https://images.pexels.com/photos/10348767/pexels-photo-10348767.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900";

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefPackage = searchParams.get("package") || "";
  const { register } = useAuth();
  const asideImg = useSiteImage("registerAside", HERITAGE_IMG_DEFAULT);

  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirm: "",
    accept: false,
  });

  const update = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e?.target?.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.password) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (form.password !== form.confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    if (!form.accept) {
      toast.error("Please accept the delegate terms to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await register({
        name: form.name,
        email: form.email,
        phone: form.phone,
        password: form.password,
      });
      toast.success("Welcome — your account is ready. Let's pick a package.");
      navigate(prefPackage ? `/payment?package=${prefPackage}` : "/payment", { replace: true });
    } catch (err) {
      toast.error(readError(err) || "Could not create account.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="relative min-h-[calc(100vh-5rem)] bg-white"
      data-testid="page-register"
    >
      <div className="mx-auto grid w-full max-w-7xl gap-10 px-5 py-14 md:px-8 md:py-20 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-2xl"
          >
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-green">
              Delegate registration
            </div>
            <h1
              className="mt-3 font-display text-4xl font-bold tracking-tight text-apicon-navy sm:text-5xl"
              data-testid="register-title"
            >
              Reserve your seat at{" "}
              <span className="italic text-apicon-green">APICON 2026</span>.
            </h1>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-gray-600">
              Create your delegate account in one step and head straight to
              payment.
            </p>

            {/* Stepper */}
            <div
              className="mt-8 flex items-center gap-3 text-sm"
              data-testid="register-stepper"
            >
              {[
                { key: "form", label: "1 · Your details" },
                { key: "done", label: "2 · Payment" },
              ].map((s, i) => (
                <div key={s.key} className="flex items-center gap-3">
                  <span
                    className={
                      "rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] " +
                      (i === 0
                        ? "bg-apicon-navy text-white"
                        : "bg-apicon-mist text-apicon-navy/50")
                    }
                  >
                    {s.label}
                  </span>
                  {i < 1 && <span className="h-px w-6 bg-gray-200" />}
                </div>
              ))}
            </div>

            <motion.form
              key="form-step"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              onSubmit={onSubmit}
              className="mt-8 rounded-3xl border border-gray-100 bg-white p-6 shadow-sm sm:p-8"
              data-testid="register-form"
            >
              <div className="grid gap-2">
                <Label htmlFor="name" className="text-sm text-apicon-navy">
                  Full name
                </Label>
                <div className="relative">
                  <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="name"
                    type="text"
                    autoComplete="name"
                    placeholder="Dr. Aparna Bora"
                    value={form.name}
                    onChange={update("name")}
                    className="h-12 rounded-xl border-gray-200 pl-10 text-sm focus-visible:ring-apicon-navy"
                    data-testid="register-input-name"
                  />
                </div>
              </div>

              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="email" className="text-sm text-apicon-navy">
                    Email
                  </Label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@hospital.in"
                      value={form.email}
                      onChange={update("email")}
                      className="h-12 rounded-xl border-gray-200 pl-10 text-sm focus-visible:ring-apicon-navy"
                      data-testid="register-input-email"
                    />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="phone" className="text-sm text-apicon-navy">
                    Phone
                  </Label>
                  <div className="relative">
                    <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <Input
                      id="phone"
                      type="tel"
                      autoComplete="tel"
                      placeholder="+91 98xxx xxxxx"
                      value={form.phone}
                      onChange={update("phone")}
                      className="h-12 rounded-xl border-gray-200 pl-10 text-sm focus-visible:ring-apicon-navy"
                      data-testid="register-input-phone"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label
                    htmlFor="password"
                    className="text-sm text-apicon-navy"
                  >
                    Password
                  </Label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <Input
                      id="password"
                      type={showPwd ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="At least 8 characters"
                      value={form.password}
                      onChange={update("password")}
                      className="h-12 rounded-xl border-gray-200 pl-10 pr-10 text-sm focus-visible:ring-apicon-navy"
                      data-testid="register-input-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 hover:text-apicon-navy"
                      aria-label="Toggle password visibility"
                      data-testid="register-toggle-password"
                    >
                      {showPwd ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="confirm" className="text-sm text-apicon-navy">
                    Confirm password
                  </Label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <Input
                      id="confirm"
                      type={showConfirm ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="Repeat your password"
                      value={form.confirm}
                      onChange={update("confirm")}
                      className="h-12 rounded-xl border-gray-200 pl-10 pr-10 text-sm focus-visible:ring-apicon-navy"
                      data-testid="register-input-confirm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 hover:text-apicon-navy"
                      aria-label="Toggle confirm password visibility"
                      data-testid="register-toggle-confirm"
                    >
                      {showConfirm ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-start gap-3">
                <Checkbox
                  id="accept"
                  checked={form.accept}
                  onCheckedChange={(v) =>
                    setForm((f) => ({ ...f, accept: !!v }))
                  }
                  className="mt-0.5 border-gray-300 data-[state=checked]:border-apicon-navy data-[state=checked]:bg-apicon-navy"
                  data-testid="register-checkbox-terms"
                />
                <Label
                  htmlFor="accept"
                  className="text-sm font-normal leading-relaxed text-gray-600"
                >
                  I agree to the APICON 2026 delegate{" "}
                  <span className="font-medium text-apicon-navy underline underline-offset-4">
                    code of conduct
                  </span>{" "}
                  and consent to receiving conference communications.
                </Label>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="mt-8 h-12 w-full rounded-full bg-apicon-gold text-base font-semibold text-apicon-navy shadow-lg shadow-apicon-gold/40 hover:bg-[#ffb400] disabled:opacity-70"
                data-testid="register-submit"
              >
                {submitting ? "Creating account…" : "Create account and continue"}
              </Button>

              <p className="mt-5 text-center text-sm text-gray-600">
                Already registered?{" "}
                <Link
                  to="/login"
                  className="font-medium text-apicon-navy underline underline-offset-4 hover:text-apicon-green"
                  data-testid="register-login-link"
                >
                  Sign in instead
                </Link>
              </p>
            </motion.form>
          </motion.div>
        </div>

        <aside className="relative hidden lg:col-span-5 lg:block">
          <div className="sticky top-28">
            <div className="relative overflow-hidden rounded-[28px] shadow-2xl shadow-apicon-navy/20 ring-1 ring-black/5">
              <img
                src={asideImg}
                alt="Tea gardens of Assam"
                className="h-[560px] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-apicon-navy via-apicon-navy/60 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-8 text-white">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white backdrop-blur">
                  <Leaf className="h-3.5 w-3.5 text-apicon-gold" /> Tinsukia ·
                  Assam
                </div>
                <div className="mt-4 font-display text-3xl font-semibold leading-tight">
                  Physicians. Tea gardens.
                  <br /> One unhurried December.
                </div>
              </div>
            </div>

            <div className="mt-6 grid gap-4">
              <div className="flex items-start gap-3 rounded-2xl border border-gray-100 bg-white p-5">
                <ShieldCheck className="mt-0.5 h-5 w-5 text-apicon-green" />
                <div>
                  <div className="text-sm font-semibold text-apicon-navy">
                    One-step signup
                  </div>
                  <div className="text-sm text-gray-600">
                    No OTP. Register once and head straight to package
                    selection.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-2xl border border-gray-100 bg-white p-5">
                <Sparkles className="mt-0.5 h-5 w-5 text-apicon-gold" />
                <div>
                  <div className="text-sm font-semibold text-apicon-navy">
                    Early-bird pricing
                  </div>
                  <div className="text-sm text-gray-600">
                    Delegate and PG registration options available.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Register;
